import {
    CreateTopicRequest,
    DiscoveryLevel,
    GetCreatorProfileResponse,
} from '../../types';
import { LitElement, css, html } from 'lit';
import { addTopicsToCreatorProfile, getCreatorProfiles, removeTopicFromCreatorProfile } from '../../helpers/api/creator-profile-api';
import { createTopicRequestFromTopic, getRedirectKeyAvailable } from '../../helpers/api/topics-api';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined } from '../../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { FormSwitch } from '../theme/form-switch';
import { RichTextEditor } from '../common/rich-text-editor';
import { Select } from '@material/mwc-select';
import { StateController } from '../../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import TurndownService from 'turndown';
import accountInfo from '../../helpers/state/account-info';
import { getRedirectKeyFromUrl } from '../../helpers/urls';
import { gptCall } from '../../helpers/api/gpt-api';
import { marked } from 'marked';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';
import { getMainPage } from '../../helpers/page-name';

@customElement('admin-publish-fragment')
export class AdminPublishFragment extends LitElement {
    amplitude_callsite: string = 'Publish Drawer';

    @query('#addCreatorProfileModal')
    addCreatorProfileModalComponent!: Dialog;

    @query('#makeMapPublic')
    private readonly makeMapPublicElement!: FormSwitch;

    @query('#discoveryLevel')
    private readonly discoveryLevelElement!: FormSwitch;

    @query('#description')
    private readonly descriptionElement!: RichTextEditor;

    @query('#discoverVanityRedirectKey')
    private readonly discoverVanityRedirectKeyElement?: TextField;

    @query('#creatorProfile')
    private readonly creatorProfileElement!: Select;

    @property({ type: Boolean })
    showSaveButton: boolean = false;

    @property({ type: Boolean })
    showRedirectSetting: boolean = false;

    @state()
    creatorProfiles: GetCreatorProfileResponse[] = [];

    @state()
    isPublic: boolean = false;

    @state()
    createTopicRequest: CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);

    discoverRedirectAvailable = true;
    discoverRedirectKey = '';

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindPointCacheState = new StateController(this, pointCache);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        if (accountInfo.isAuthenticated) {
            this.creatorProfiles = await getCreatorProfiles(this.amplitude_callsite);
        }

        if (topicData.discoveryLevel === DiscoveryLevel.Private) {
            this.makeMapPublicElement.checked = false;
            this.discoveryLevelElement.checked = false;
        } else {
            this.isPublic = true;
            this.makeMapPublicElement.checked = true;
            this.discoveryLevelElement.checked =
            topicData.discoveryLevel === DiscoveryLevel.AddpointPublic;
        }

        this.discoverRedirectKey = getRedirectKeyFromUrl(topicData.topic?.discover_details_link);
        if (isDefined(this.discoverVanityRedirectKeyElement)) {
            this.discoverVanityRedirectKeyElement.value = this.discoverRedirectKey;
        }

        await this.descriptionElement.updateComplete;
        if (this.descriptionElement.root === null) {
            return;
        }
        this.descriptionElement.root.innerHTML = marked.parse(topicData.topic?.description === undefined || topicData.topic?.description === '' ? await this.getGPTDescription() : topicData.topic?.description);
    }

    private async getGPTDescription() {
        // if map does not have a description and has at least three points call the gpt_passthrough api to create a description
        if ((topicData.topic?.description === '' || topicData.topic?.description === undefined) && pointCache.points.length >= 3) {
            const generic_name = !!(topicData.topic?.name === 'My Proxi Map' || topicData.topic?.name === 'New Map from Template');
            const map_name: string = generic_name ? '' : (topicData.topic?.name ?? '');
            const search: string = topicData.topic?.default_map_center.search ?? '';
            const pointsLength: number = pointCache.points.length;
            const hasPoints: boolean = pointsLength > 0;
            // create string list with point name, point category, point description, and point location search. end with a line break.
            const pointsList: string = hasPoints ? pointCache.points.slice(0,8).map(p => `${p.name as string}, ${p.description as string}, ${p.location.search}\n`).join('') : '';
            const topicCategories = topicData.topic?.categories.map(c => c.name).join(', ');

            const prompt: string =
                `Help me generate a paragraph description about an interactive map ${generic_name ? '' : 'with the name ' + map_name} and categories: ${topicCategories ?? ''}.
                The map is centered in ${search} and has ${pointsLength} points.
                Here is a sample of some of the points on the map:
                ${hasPoints ? pointsList : ''}

                Try not to just list the points or places, rather write something enticing about the map that is SEO friendly. Max 5 sentences.`;

            const response = await gptCall(this.amplitude_callsite, prompt);

            // the response is a stringigied json, unstringify it and return the description
            return response;
        };
        return '';
    }


    private async onAddCreatorProfile(e: any) {
        this.creatorProfiles = [...this.creatorProfiles, e.detail.creatorProfile];
    }

    private async togglePublic() {
        this.isPublic = !this.isPublic;

        if (this.isPublic) {
            if (isDefined(this.discoverVanityRedirectKeyElement)) {
                const new_discover_vanity_link = topicData.name.replaceAll(' ', '_');
                this.discoverVanityRedirectKeyElement.value = new_discover_vanity_link;
            }
            this.createTopicRequest.discovery_level = DiscoveryLevel.ReadPublic;
        } else {
            this.createTopicRequest.discovery_level = DiscoveryLevel.Private;
        }

        await topicData.fetchAndUpdateTopicData(
            {
                discovery_level: this.createTopicRequest.discovery_level,
            },
            this.amplitude_callsite
        );

    }

    private async toggleDiscoveryLevel() {
        this.createTopicRequest.discovery_level = this.discoveryLevelElement.checked === true ? DiscoveryLevel.AddpointPublic : DiscoveryLevel.ReadPublic;

        await topicData.fetchAndUpdateTopicData(
            {
                discovery_level: this.createTopicRequest.discovery_level,
            },
            this.amplitude_callsite
        );
    }

    // adding map to creator profile doesnt change the topic... so we cant dispatch a SettingsChangeEvent. need to figure out this edge case
    private async onCreatorProfileCheckboxClicked(creatorProfile: GetCreatorProfileResponse, removeFollow: boolean) {
        if (removeFollow) {
            await removeTopicFromCreatorProfile(this.amplitude_callsite, creatorProfile._id.$oid, topicData.topicId);
        } else {
            await addTopicsToCreatorProfile(this.amplitude_callsite, creatorProfile._id.$oid, [topicData.topicId]);
        }
    }

    private async handleDescriptionChange() {
        const turndownService = new TurndownService();
        let description = turndownService.turndown(this.descriptionElement.root?.innerHTML ?? '');
        description = new DOMParser()
            .parseFromString(description, 'text/html')
            .documentElement.textContent ?? '';

        await topicData.fetchAndUpdateTopicData(
            {
                description,
            },
            this.amplitude_callsite
        );
    }

    protected render() {
        return html`
            <div id="container">
                <p class="info-paragraph">
                    Make your map searchable so people can find it on Proxi Discover and Google Search. Unlisted maps still work via direct link — they just won't appear in search.
                </p>
                <h4 class="sub-title">Searchable on Proxi Discover &amp; Google</h4>

                <div class="toggleSection flexContainer alignCenter">
                    <span class="toggleSection flexContainer alignCenter">
                        Unlisted
                        <mwc-icon>lock</mwc-icon>
                    </span>
                    <form-switch id="makeMapPublic" name="makeMapPublic" @change="${this.togglePublic}"></form-switch>
                    <span class="toggleSection flexContainer alignCenter">
                        Searchable
                        <mwc-icon>public</mwc-icon>
                    </span>
                </div>
                <span class=${!this.isPublic ? 'settings--private': ''}>
                    <h4 class="sub-title">Share Map Version</h4>
                    <div class="toggleSection flexContainer alignCenter">
                        <form-tooltip
                            tipwidth="100px"
                            leftPadding="40%"
                            message="Viewers can explore and interact with this map, but cannot add points or make changes.">
                            <div class="optionItem flexContainer alignCenter">
                                <span class="optionLabel">View Only</span>
                                <mwc-icon>info_outline</mwc-icon>
                            </div>
                        </form-tooltip>
                        <form-switch id="discoveryLevel" name="discoveryLevel" @change=${this.toggleDiscoveryLevel}></form-switch>
                        <form-tooltip
                            tipwidth="160px"
                            leftPadding="30%"
                            message="Viewers can interact with the map and suggest points to add to the map.">
                            <div class="optionItem flexContainer alignCenter">
                                <span class="optionLabel">Collaborative</span>
                                <mwc-icon>info_outline</mwc-icon>
                            </div>
                        </form-tooltip>
                    </div>

                    <!-- Rich text editor for description -->
                    <h4 class="sub-title">Description</h4>
                    <p>
                        We will display this description on the Discovery map page and will use it to improve search results
                    </p>
                    <rich-text-editor @blur=${this.handleDescriptionChange} id="description" style="margin-top: 0.25rem"></rich-text-editor>
                </span>
            </div>
            <div class="addon__footer">
                ${this.showSaveButton ? html`<mwc-button class="save button" @click="${this.save}" raised>Save</mwc-button>` : ''}
            </div>
            <error-toast id="errorToast"></error-toast>
            <mwc-dialog id="addCreatorProfileModal" class="dialog" hideActions>
                <modal-dialog size="lg" @close="${() => this.addCreatorProfileModalComponent?.close()}">
                    <add-creator-profile-modal
                        id="addCreatorProfileModalContent"
                        slot="body"
                        @on-add-creator-profile="${this.onAddCreatorProfile}"
                        @close="${() => this.addCreatorProfileModalComponent?.close()}">
                    </add-creator-profile-modal>
                </modal-dialog>
            </mwc-dialog>

        `;
    }

    private async save() {
        await topicData.fetchAndUpdateTopicData(this.createTopicRequest, this.amplitude_callsite);
        this.dispatchEvent(new CustomEvent('close-fragment'));
    }

    private readonly debouncedDiscoverVanityRedirectKeyChanged = debounce(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.vanityRedirectKeyChanged.bind(this),
        1000
    );

    private async vanityRedirectKeyChanged() {
        if (isDefined(this.discoverVanityRedirectKeyElement) && this.discoverVanityRedirectKeyElement.value === this.discoverRedirectKey) {
            this.discoverRedirectAvailable = true;
            this.discoverVanityRedirectKeyElement.reportValidity();
            return;
        }

        if (isDefined(this.discoverVanityRedirectKeyElement)) {
            const element = this.discoverVanityRedirectKeyElement;

            const redirect_key = element.value;

            if (element.validity.patternMismatch || redirect_key.length < 6) {
                element.reportValidity();
                return;
            }

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                const available = await getRedirectKeyAvailable(
                    this.amplitude_callsite,
                    redirect_key
                );

                this.discoverRedirectAvailable = available;

                element.reportValidity();
            }, 'An error has occurred.');
        }
    }

    private readonly isDiscoverVanityRedirectKeyAvailable = this.isVanityRedirectKeyAvailable.bind(
        this
    );

    private isVanityRedirectKeyAvailable(
        redirect_key: string,
        nativeValidity: any
    ) {
        const available = this.discoverRedirectAvailable;
        if (
            redirect_key === '' ||
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            (available && nativeValidity.valid && redirect_key.length >= 6)
        ) {
            return {
                valid: true,
            };
        }

        return {
            valid: false,
        };
    }

    static get styles() {
        return css`
            .settings--private {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            #container {
                flex-direction: column;
                margin-bottom: 1rem;
                padding-right: 30px;
            }

            /* View-only/collaborative toggle */
            .toggleSection {
                padding-top: 0.25rem;
                justify-content: center;
            }

            .flexContainer {
                display: flex;
            }

            .alignCenter {
                align-items: center;
            }

            .optionLabel {
                color: var(--theme-input-color);
                font-size: 0.9rem;
                white-space: nowrap;
                margin-right: 0.5rem;
            }

            #discoveryLevel {
                margin: 0 0.5rem;
            }

            .input {
                padding: auto 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            mwc-textfield.rounded {
                border-radius: 50px;
                height: 1.5rem;
            }

            .helpSubHeader {
                margin-bottom: 0px;
                font-size: 0.75rem;
                color: var(--theme-input-color);
            }

            .helpSubHeaderLink {
                text-decoration: underline;
                color: var(--theme-input-color);
                font-weight: bold;
                cursor: pointer;
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

            .form-label {
                line-height: 1rem;
                font-size: 0.8rem;
                color: var(--theme-input-color);
            }

            @media only screen and (min-width: 551px) {
                #container {
                    overflow: hidden auto;
                    max-height: calc(100vh - 162px);
                }

                /* width */
                #container::-webkit-scrollbar {
                    width: 5px;
                }

                /* Track */
                #container::-webkit-scrollbar-track {
                    background: var(--theme-light-scrollbar-color);
                }

                /* Handle */
                #container::-webkit-scrollbar-thumb {
                    background: var(--main-moon-color);
                }
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }
}
